import {
  Box, DashboardModule, Form, Link, StatusBadge, Text,
} from '@domain-group/suit-theme'

import { digObject, formatDate } from '@domain-group/javascript-utils'

import useStatus from '@hooks/useStatus'
import type { UseInvoicePayload } from '@hooks/useInvoice'

interface getStatusKeyParams {
  isApproved: boolean,
  isVoid: boolean,
}

const getStatusKey = (params: getStatusKeyParams) => {
  const { isApproved, isVoid } = params
  if (isVoid) return 'cancelled'
  if (isApproved) return 'approved'
  return 'pending'
}

interface BillingDetailsProps {
  invoicePayload: UseInvoicePayload,
}

const BillingDetails = (props: BillingDetailsProps) => {
  const { invoicePayload } = props
  const {
    callbacks: {
      manageInvoiceBillingDetails,
    },
    invoice,
  } = invoicePayload

  const {
    currency_code, dates, notes, order_title, reference, approved: isApproved, void: isVoid,
  } = invoice

  const createdDate = digObject(dates, 'created.date', '')
  const formattedCreatedDate = formatDate(createdDate, 'dd/MM/yyyy', 'd MMMM yyyy')

  const dueDate = digObject(dates, 'due.date', '')
  const formattedDueDate = formatDate(dueDate, 'dd/MM/yyyy', 'd MMMM yyyy')

  const statusPayload = useStatus({}, { defaultStatusKey: getStatusKey({ isApproved, isVoid }) })
  // @ts-expect-error TS2339
  const { color, status: { title } } = statusPayload

  const shouldShowManageButton = !isVoid && !isApproved

  return (
    <DashboardModule
      contentBoxProps={{ flexDirection: 'column' }}
      title="Billing Details"
      headerRight={shouldShowManageButton && (
        <Link onClick={() => manageInvoiceBillingDetails()} textProps={{ fontSize: 'small' }}>
          Manage
        </Link>
      )}
    >
      <Box
        borderBottom="1px solid"
        borderColor="lineColor"
        fontSize="small"
        padding="large"
        disableHover
      >
        <Form.Field
          boxProps={{ flex: 1 }}
          labelProps={{ style: { alignItems: 'center' } }}
          label="Order Title"
        >
          <Text>
            {order_title}
          </Text>
        </Form.Field>

        <Box flexDirection="column" alignItems="end" width="auto">
          <StatusBadge
            key={invoice.id}
            boxProps={{ marginBottom: '5px', paddingY: 0 }}
            color={color}
            ghost
            text={title}
          />

          <Text>
            {formattedCreatedDate}
          </Text>
        </Box>
      </Box>

      <Box fontSize="small" padding="large" flexDirection="column">
        <Box alignItems="center" justifyContent="space-between">
          <Text color="bodyFontLightColor" fontSize="xsmall" letterSpacing="1px">Due Date</Text>

          <Text>{formattedDueDate || 'N/A'}</Text>
        </Box>

        <Box alignItems="center" justifyContent="space-between" marginTop="large">
          <Text color="bodyFontLightColor" fontSize="xsmall" letterSpacing="1px">Reference</Text>

          <Text maxWidth="220px" variant="ellipsis">{reference}</Text>
        </Box>

        <Box alignItems="center" justifyContent="space-between" marginTop="large">
          <Text color="bodyFontLightColor" fontSize="xsmall" letterSpacing="1px">Currency</Text>

          <Text maxWidth="220px" variant="ellipsis">{currency_code}</Text>
        </Box>

        <Form.Field label="Notes" marginTop="large">
          <Text>{notes || 'No Notes Present.'}</Text>
        </Form.Field>
      </Box>
    </DashboardModule>
  )
}
export default BillingDetails
